import { Injectable } from '@angular/core';
import { RxDatabase, createRxDatabase, addRxPlugin } from 'rxdb';
import { CentralDatabaseDbContext } from './schemas/central-database.dbcontext';
import { CentralDatabaseKeys, centralTableInfoSchema } from './schemas';
import { centralSchema } from './schemas/central-schema';
import { RxdbAdaptorProvider } from '../infrastructure/rxdb-adaptor.provider';
import { WorkspaceModel } from './models';
import { convertStringToArray } from '@shared';
import { DeviceIdProvider } from '@database/workspace';
import { centralLocalCredentialsSchema } from './schemas/central-local-credentials-schema';
import { centralApiConnectionsSchema } from './schemas/central-api-connections-schema';
import { RxDBMigrationPlugin } from 'rxdb/plugins/migration';
addRxPlugin(RxDBMigrationPlugin);

@Injectable()
export class CentralDatabaseProvider {
	private database: RxDatabase<CentralDatabaseDbContext>;

	public constructor(
		private adapterProvider: RxdbAdaptorProvider,
		private deviceIdProvider: DeviceIdProvider
	) {}

	public getDatabase() {
		if (this.database) {
			return this.database;
		}
		throw new Error('Database instance was not created. Make sure the database is created');
	}

	public async create() {
		if (this.database) {
			return this.database;
		}

		const adapter = this.adapterProvider.getAdaptor();

		this.database = await createRxDatabase({
			name: CentralDatabaseKeys.centraldatabase,
			storage: adapter,
			ignoreDuplicate: true,
			localDocuments: true
		});

		const workspacesCollection = {};

		workspacesCollection[CentralDatabaseKeys.workspaces] = {
			schema: centralSchema,
			migrationStrategies: {
				1: (oldDoc: WorkspaceModel) => {
					oldDoc.pendingDelete = false;
					return oldDoc;
				},
				2: async (oldDoc) => {
					try {
						for (const entity of oldDoc.fusionEntities) {
							entity.primary_key = convertStringToArray(entity.primary_key);
						}
						const configHeader = oldDoc.fussionConfigHeaders[0];
						configHeader.device_id = await this.deviceIdProvider.getDeviceId();
						
						configHeader.registration_token = '';

						return oldDoc;
					} catch (error) {
						//todo: show a message to the user that the upgrade failed
						return oldDoc;
					}
				},
				3: (oldDoc: WorkspaceModel) => {
					oldDoc.apiConnectionId = null;
					oldDoc.importedFromFile = true;
					return oldDoc;
				}
			}
		};

		workspacesCollection[CentralDatabaseKeys.tableInfo] = {
			schema: centralTableInfoSchema
		};

		workspacesCollection[CentralDatabaseKeys.localCredentials] = {
			schema: centralLocalCredentialsSchema
		};

		workspacesCollection[CentralDatabaseKeys.apiConnections] = {
			schema: centralApiConnectionsSchema
		};

		await this.database.addCollections(workspacesCollection);

		return this.database;
	}
}
